export const LANGUAGE_STORAGE_KEY = 'i18nextLng'

export const SUPPORTED_LANGUAGES = ['en', 'ja'] as const

export type AppLanguage = typeof SUPPORTED_LANGUAGES[number]

export const isAppLanguage = (value: unknown): value is AppLanguage => {
  return value === 'en' || value === 'ja'
}

export type ResolveLanguageParams = Readonly<{
  userLanguage?: string | null
  storedLanguage?: string | null
  browserLanguages?: readonly string[] | null
}>

const coerceLanguage = (language?: string | null): AppLanguage | undefined => {
  if (!language) return undefined
  if (isAppLanguage(language)) return language

  const normalized = language.toLowerCase()
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en'
  if (normalized === 'ja' || normalized.startsWith('ja-')) return 'ja'

  return undefined
}

const isEnglishVariant = (language?: string | null): boolean => {
  return coerceLanguage(language) === 'en'
}

export const resolveLanguage = ({
  userLanguage,
  storedLanguage,
  browserLanguages,
}: ResolveLanguageParams): AppLanguage => {
  return coerceLanguage(userLanguage)
    ?? coerceLanguage(storedLanguage)
    ?? coerceLanguage(browserLanguages?.[0])
    ?? 'ja'
}

export const getStoredLanguage = (): string | null => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

export const cacheUserLanguage = (language: string): void => {
  if (!isAppLanguage(language)) return

  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // ignore storage failures (private mode, etc.)
  }
}

const browserLanguages = (): string[] => {
  if (typeof navigator === 'undefined') return []

  if (navigator.languages && navigator.languages.length > 0) {
    return Array.from(navigator.languages)
  }

  return navigator.language ? [navigator.language] : []
}

export const detectClientLanguage = (): AppLanguage => {
  return resolveLanguage({
    storedLanguage: getStoredLanguage(),
    browserLanguages: browserLanguages(),
  })
}

export const languageDetector = {
  type: 'languageDetector' as const,
  async: false,
  init: () => undefined,
  detect: () => detectClientLanguage(),
  cacheUserLanguage,
}

export const setAppLanguage = async (
  language: AppLanguage,
  options?: Readonly<{ persistToUser?: boolean }>
): Promise<void> => {
  const { i18n } = await import('../components/i18n')
  await i18n.changeLanguage(language)

  if (options?.persistToUser) {
    const { usersRepository } = await import('../repositories/users_repository')
    await usersRepository.updateLanguage({
      id: 'me',
      params: { language },
    })
  }

  cacheUserLanguage(language)
}

export const applyUserLanguage = async (userLanguage?: string | null): Promise<void> => {
  if (!isAppLanguage(userLanguage)) return

  await setAppLanguage(userLanguage)
}

export const nextAppLanguage = (currentLanguage: string): AppLanguage => {
  return isEnglishVariant(currentLanguage) || currentLanguage === 'en' ? 'ja' : 'en'
}
